import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { showModal } from 'actions/modal';

import config from 'config';

import { useCurrentTeam, useCurrentPlanProps } from 'shared-hooks/team';
import { useSubscriptionIsExpired, useIsOnTrial, useRemainingTrial } from 'shared-hooks/billing';
import { trackEvent } from 'services/events-tracker-service';
import { getSessionLength, logout, openIntercom } from 'globals-service';

import ThemeSwitch from 'components/ThemeSwitch';
import AvatarBase from 'components/avatar-base';
import TeamsList from 'components/TeamsList';

import IconSettings from 'icons/icon-settings';
import IconApps from 'icons/icon-apps';
import IconTemplate from 'icons/icon-template';
import IconAddBlock from 'icons/icon-add-block';
import IconPlusSquare from 'icons/icon-plus-square';
import IconBoltSquare from 'icons/icon-bolt-square';
import IconHelpSquare from 'icons/icon-help-square';
import IconStarSimple from 'icons/icon-star-simple';
import IconDiscussionAlt from 'icons/icon-discussion-alt';
import IconLogout from 'icons/icon-logout';
import IconUpDown from 'icons/icon-up-down';
import IconProfileSquare from 'icons/icon-profile-square';

import './AppDropdown.styles.css';

const Group = ({ children }) => {
    return (
        <div className="app-dropdown-group">
            { children }
        </div>
    );
}

const NavItem = ({ icon, text, href, cta, onClick }) => {
    const Element = href ? 'a' : 'div';
    const props = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};

    return (
        <Element className="app-dropdown-nav-item" onClick={onClick} {...props}>
            <div className="nav-item-icon">
                { icon }
            </div>

            <div className="nav-item-body">
                { text }
            </div>

            {
                cta ? <button className="btn btn-xsmall btn-success btn-single-line">✨ { cta }</button> : null
            }
        </Element>
    );
}

const TeamPreview = ({ onSwitchClick }) => {
    const currentTeam = useCurrentTeam();

    return (
        <div className="app-dropdown-team-preview">
            <div className="team-preview-avatar">
                <AvatarBase
                    text={currentTeam.initials}
                    image={currentTeam.logo}
                    color={currentTeam.color}
                    foregroundColor={currentTeam.secondary_color}
                />
            </div>

            <div className="team-preview-body">
                <h2>{ currentTeam.name }</h2>

                <h5>{ currentTeam.cname || `${currentTeam.url}.${config.APP_DOMAIN}` }</h5>
            </div>

            <div className="team-preview-foot">
                <div className="team-preview-switch" onClick={onSwitchClick}>
                    <IconUpDown />
                </div>
            </div>
        </div>
    );
}

const AppDropdown = ({ requestClose }) => {
    const { t } = useTranslation(['settings', 'template', 'apps', 'import', 'team', 'billing']);
    const history = useHistory();
    const location = useLocation();
    const [switchingTeam, setSwitchingTeam] = useState(false);
    const currentPlanProps = useCurrentPlanProps();
    const subscriptionIsExpired = useSubscriptionIsExpired();
    const subscriptionIsOnTrial = useIsOnTrial();
    const subscriptionRemainingTrial = useRemainingTrial();

    const handleSettingsClick = () => {
        history.push({
            pathname: '/settings/profile',
            state: {
                closeUrl: location.pathname
            }
        });

        requestClose();
    }

    const handleAppsClick = () => {
        history.push({
            pathname: '/settings/apps',
            state: {
                closeUrl: location.pathname
            }
        });

        requestClose();
    }

    const handleTemplatesClick = () => {
        showModal('TEMPLATE_CENTER');
        requestClose();
    }

    const handleImportProjectsClick = () => {
        showModal('IMPORT_PROJECTS');
        requestClose();
    }

    const handleCreateTeamClick = () => {
        showModal('CREATE_TEAM');
        requestClose();
    }

    const handleLogoutClick = () => {
        requestClose();

        trackEvent('USER.LOGOUT', {
            sessionLength: getSessionLength() / 1000 / 60
        }).then(logout);
    }

    const handleContactClick = () => {
        openIntercom();
        requestClose();
    }

    const handleUpgradeClick = useCallback(() => {
        showModal('SWITCH_PLAN');
        requestClose();
    }, [requestClose]);

    const handleSwitchClick = () => {
        setSwitchingTeam(prevState => !prevState);
    }

    const billingElement = useMemo(() => {
        if (currentPlanProps?.appSumo || currentPlanProps?.price?.lifetime) {
            return null;
        }

        if (currentPlanProps?.price?.free) {
            return (
                <Group>
                    <NavItem
                        icon={<IconProfileSquare color="#f89654" />}
                        text={ t('billing:planName', { plan: t('billing:plans.Free.title') }) }
                        cta={ t('billing:Upgrade') }
                        onClick={handleUpgradeClick}
                    />
                </Group>
            );
        }

        if (subscriptionIsExpired) {
            return (
                <Group>
                    <NavItem
                        icon={<IconProfileSquare color="#f89654" />}
                        text={ t('billing:expiredSubscription') }
                        cta={ t('billing:Activate') }
                        onClick={handleUpgradeClick}
                     />
                </Group>
            );
        }

        if (subscriptionIsOnTrial) {
            return (
                <Group>
                    <NavItem
                        icon={<IconProfileSquare color="#f89654" />}
                        text={ t('billing:Days left on trial', { count: subscriptionRemainingTrial }) }
                        cta={ t('billing:Upgrade') }
                        onClick={handleUpgradeClick}
                     />
                </Group>
            );
        }

        if (currentPlanProps?.level < 30) {
            return (
                <Group>
                    <NavItem
                        icon={<IconProfileSquare color="#f89654" />}
                        text={ t('billing:upgradeTo', { plan: t('billing:plans.Business.title') }) }
                        onClick={handleUpgradeClick}
                    />
                </Group>
            );
        }

        return null;
    }, [currentPlanProps, subscriptionIsExpired, handleUpgradeClick]);

    if (switchingTeam) {
        return (
            <div className="app-dropdown">
                <div className="app-dropdown-head">
                    <TeamPreview onSwitchClick={handleSwitchClick} />
                </div>

                <div className="app-dropdown-body">
                    <Group>
                        <TeamsList showDirectTeams={true} showLinkedTeams={true} withSwitch={true} onTeamSelect={requestClose} />
                    </Group>
                </div>
            </div>
        );
    }

    return (
        <div className="app-dropdown">
            <div className="app-dropdown-head">
                <TeamPreview onSwitchClick={handleSwitchClick} />

                <ThemeSwitch />
            </div>

            <div className="app-dropdown-body">
                <Group>
                    <NavItem icon={<IconSettings />} text={ t('Settings') } onClick={handleSettingsClick} />

                    <NavItem icon={<IconApps />} text={ t('apps:App Center') } onClick={handleAppsClick} />

                    <NavItem icon={<IconTemplate />} text={ t('template:title') } onClick={handleTemplatesClick} />
                </Group>

                <Group>
                    <NavItem icon={<IconAddBlock />} text={ t('import:Import Projects') } onClick={handleImportProjectsClick} />

                    <NavItem icon={<IconPlusSquare />} text={ t('team:New Workspace') } onClick={handleCreateTeamClick} />

                    <NavItem icon={<IconBoltSquare />} text={ t('team:Connect Workspace') } />
                </Group>

                <Group>
                    <NavItem icon={<IconHelpSquare />} text={ t('common:Help') } href="http://help.niftypm.com" />

                    <NavItem icon={<IconStarSimple />} text={ t('What’s new') } href="https://changelog.niftypm.com/" />

                    <NavItem icon={<IconDiscussionAlt />} text={ t('contactUs') } onClick={handleContactClick} />
                </Group>

                { billingElement }

                <Group>
                    <NavItem icon={<IconLogout />} text={ t('Sign Out') } onClick={handleLogoutClick} />
                </Group>
            </div>
        </div>
    );
}

export default AppDropdown;
